import { Component, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, takeUntil, combineLatest } from 'rxjs';
import { LocalizationModule, LocalizationService, ConfigStateService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { ImpersonationService } from '../../services/impersonation.service';
import { ImpersonationStatusDto } from '../../models/impersonation.models';

@Component({
  selector: 'app-impersonation-banner',
  standalone: true,
  imports: [CommonModule, LocalizationModule],
  template: `
    <div *ngIf="shouldShowBanner" class="impersonation-banner">
      <div class="container-fluid">
        <div class="d-flex align-items-center justify-content-between py-2">
          <div class="d-flex align-items-center">
            <i class="fas fa-user-secret me-2"></i>
            <span>{{ bannerText }}</span>
            <span *ngIf="expiresIn" class="ms-2 badge bg-warning text-dark">
              {{ expiresIn }}
            </span>
          </div>
          <button
            class="btn btn-sm btn-outline-light"
            [disabled]="exiting"
            (click)="exitImpersonation()"
          >
            <span *ngIf="exiting" class="spinner-border spinner-border-sm me-1"></span>
            {{ '::Impersonation:ExitButton' | abpLocalization }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .impersonation-banner {
      background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
      color: white;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 1050;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
    :host {
      display: contents;
    }
  `],
})
export class ImpersonationBannerComponent implements OnInit, OnDestroy {
  isImpersonating = false;
  isAuthenticated = false;
  status: ImpersonationStatusDto | null = null;
  bannerText = '';
  expiresIn = '';
  exiting = false;

  private destroy$ = new Subject<void>();
  private timerInterval: any;
  private readonly BANNER_HEIGHT = '50px'; // Approximate banner height

  constructor(
    private impersonationService: ImpersonationService,
    private configStateService: ConfigStateService,
    private localization: LocalizationService,
    private toaster: ToasterService,
    private router: Router,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Only show banner when user is authenticated AND has valid impersonation
   */
  get shouldShowBanner(): boolean {
    return this.isAuthenticated && this.isImpersonating;
  }

  ngOnInit(): void {
    // Subscribe to both authentication state and impersonation status
    combineLatest([
      this.configStateService.getDeep$('currentUser.isAuthenticated'),
      this.impersonationService.status$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([isAuthenticated, status]) => {
        const wasShowing = this.shouldShowBanner;
        this.isAuthenticated = !!isAuthenticated;
        this.status = status;
        this.isImpersonating = status?.isImpersonating ?? false;

        // Update body padding based on banner visibility
        if (this.shouldShowBanner && !wasShowing) {
          this.addBodyPadding();
        } else if (!this.shouldShowBanner && wasShowing) {
          this.removeBodyPadding();
        }

        if (this.shouldShowBanner) {
          this.updateBannerText();
          this.startExpirationTimer();
        } else {
          this.clearTimer();
        }
      });
  }

  /**
   * Add padding to body to prevent content from being hidden behind the banner
   */
  private addBodyPadding(): void {
    this.renderer.setStyle(this.document.body, 'padding-top', this.BANNER_HEIGHT);
  }

  /**
   * Remove the body padding when banner is hidden
   */
  private removeBodyPadding(): void {
    this.renderer.removeStyle(this.document.body, 'padding-top');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearTimer();
    this.removeBodyPadding(); // Clean up body padding
  }

  private updateBannerText(): void {
    if (this.status?.impersonatedUserFullName || this.status?.impersonatedUserName) {
      const userName = this.status.impersonatedUserFullName || this.status.impersonatedUserName;
      this.localization
        .get('::Impersonation:BannerText')
        .subscribe(template => {
          this.bannerText = template.replace('{0}', userName || '');
        });
    }
  }

  private startExpirationTimer(): void {
    this.clearTimer();
    if (!this.status?.expiresAt) return;

    this.updateExpiresIn();
    this.timerInterval = setInterval(() => {
      this.updateExpiresIn();
    }, 1000);
  }

  private updateExpiresIn(): void {
    if (!this.status?.expiresAt) {
      this.expiresIn = '';
      return;
    }

    const now = new Date();
    // Ensure we parse the date as UTC if it doesn't have timezone info
    let expiresAtStr = this.status.expiresAt;
    if (typeof expiresAtStr === 'string' && !expiresAtStr.endsWith('Z') && !expiresAtStr.includes('+')) {
      expiresAtStr = expiresAtStr + 'Z'; // Treat as UTC
    }
    const expires = new Date(expiresAtStr);
    const diff = expires.getTime() - now.getTime();

    if (diff <= 0) {
      this.expiresIn = '';
      this.exitImpersonation();
      return;
    }

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (minutes > 0) {
      this.expiresIn = `${minutes}m ${seconds}s`;
    } else {
      this.expiresIn = `${seconds}s`;
    }
  }

  private clearTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  exitImpersonation(): void {
    this.exiting = true;
    this.impersonationService.endImpersonation().subscribe({
      next: () => {
        this.exiting = false;
        this.toaster.success('::Impersonation:Ended');
        this.router.navigate(['/']);
      },
      error: () => {
        // Force clear local state even if API call fails
        this.impersonationService.forceEndImpersonation().subscribe({
          next: () => {
            this.exiting = false;
            this.toaster.info('::Impersonation:Error:SessionExpired');
            this.router.navigate(['/']);
          },
          error: () => {
            this.exiting = false;
            this.router.navigate(['/']);
          }
        });
      },
    });
  }
}
